import axios from "axios";
import { CognitoUserPool } from "amazon-cognito-identity-js";
import { clearAuthStorage } from "./authStorage";

const baseURL = process.env.REACT_APP_API_URL || "https://api.gameyoursat.com";

const api = axios.create({
	baseURL: baseURL,
});

let userPoolPromise = null;
let refreshPromise = null;

const authExemptPaths = [
  "/api/config/cognito",
  "/api/config/cognito/",
  "/api/user/sync_oauth_user",
  "/api/user/sync_oauth_user/",
];

const authExemptPrefixes = [
  "/api/user/get_user_by_email/",
];

const isAuthExemptRequest = (config = {}) => {
  try {
    const pathname = new URL(config.url || "", config.baseURL || baseURL).pathname;
    return authExemptPaths.includes(pathname) ||
      authExemptPrefixes.some((prefix) => pathname.startsWith(prefix));
  } catch {
    return false;
  }
};

const getUserPool = async () => {
  if (!userPoolPromise) {
    userPoolPromise = axios
      .get("/api/config/cognito/", { baseURL })
      .then((res) => {
        if (!res.data.USER_POOL_ID || !res.data.CLIENT_ID) {
          throw new Error("Missing Cognito configuration");
        }

        return new CognitoUserPool({
          UserPoolId: res.data.USER_POOL_ID,
          ClientId: res.data.CLIENT_ID,
        });
      })
      .catch((error) => {
        userPoolPromise = null;
        throw error;
      });
  }  

  return userPoolPromise;
};

const redirectToLogin = () => {
  if (typeof window === "undefined") return;

  const currentURL = `${window.location.pathname}${window.location.search}`;

  if (!["/", "/login", "/signup", "/forgot-password", "/auth/callback"].includes(window.location.pathname)) {
    localStorage.setItem("redirect_url", currentURL);
  }

  if (window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
};

const getFreshIdToken = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const userPool = await getUserPool();
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      clearAuthStorage();
      return null;
    }

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err || !session || !session.isValid()) {
          clearAuthStorage();
          reject(err || new Error("Cognito session is not valid"));
          return;
        }

        const freshIdToken = session.getIdToken().getJwtToken();
        localStorage.setItem("idToken", freshIdToken);
        resolve(freshIdToken);
      });
    });
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

api.interceptors.request.use(async (config) => {
  if (isAuthExemptRequest(config)) {
    return config;
  }
  const hasStoredAuth = localStorage.getItem("idToken") || localStorage.getItem("sat_user");

  if (hasStoredAuth) {
    let freshToken = null;

    try {
      freshToken = await getFreshIdToken();
    } catch {
      clearAuthStorage();
    }

    if (!freshToken) {
      redirectToLogin();
      throw new Error("Session expired");
    }

    config.headers.Authorization = `Bearer ${freshToken}`;
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const detail = error.response?.data?.detail;

    const isExpiredToken = error.response?.status === 403 && detail === "Token has expired.";

    if (isExpiredToken && originalRequest && !originalRequest._retry && !isAuthExemptRequest(originalRequest)) {
      originalRequest._retry = true;

      try {
        const freshToken = await getFreshIdToken();

        if (freshToken) {
          originalRequest.headers.Authorization = `Bearer ${freshToken}`;
          return api(originalRequest);
        }
      } catch {
        clearAuthStorage();
      }

      clearAuthStorage();
      redirectToLogin();
    }
    return Promise.reject(error);
  }
);

export default api;
